/**
 * What a learner is told when the server refuses them — starting a session,
 * signing in, or holding a session that has expired.
 *
 * The code is read, never the server's message: those are written for someone
 * reading a GraphQL response (`aidErrors.ts` gives the same reason). Every
 * string here is copy this client owns.
 */
import { msg, type Message } from '../i18n/t.ts';
import { codeFromError } from '../views/aidErrors.ts';

type RelayThrownError = {
  source?: { errors?: ReadonlyArray<{ extensions?: Record<string, unknown> } | null | undefined> | null };
};

/** `limits.rs` puts the seconds until the limit lifts beside `RATE_LIMITED`. */
function retryAfterFromError(error: unknown): number | null {
  const after = (error as RelayThrownError | null | undefined)?.source?.errors?.[0]?.extensions?.retryAfter;
  return typeof after === 'number' && after >= 0 ? after : null;
}

/** A wait in words, rounded up to the minute: "2 minutes", never "1.5". */
export function waitCopy(seconds: number | null): Message {
  if (seconds === null) return msg('refusal-wait-unknown');
  if (seconds < 45) return msg('refusal-wait-seconds');
  return msg('refusal-wait-minutes', { count: Math.ceil(seconds / 60) });
}

/**
 * Why `startSession` refused. The codes are `errors.rs`'s: `FORBIDDEN` is the
 * unverified-email gate (auth §13), `AT_CAPACITY` every model slot taken, and
 * `INTERNAL_SERVER_ERROR` what `unavailable()` sends when the model server is
 * down. No code at all is a request that never reached the server.
 */
export function whyNotStarted(error: unknown): Message {
  switch (codeFromError(error)) {
    case 'FORBIDDEN':
      return msg('refusal-start-forbidden');
    case 'AT_CAPACITY':
      return msg('refusal-start-at-capacity');
    case 'INTERNAL_SERVER_ERROR':
      return msg('refusal-start-unavailable');
    case 'RATE_LIMITED':
      return msg('refusal-start-rate-limited', { wait: waitCopy(retryAfterFromError(error)) });
    case 'UNAUTHENTICATED':
      return msg('refusal-start-unauthenticated');
    case null:
      return msg('refusal-unreachable');
    default:
      return msg('refusal-start-failed');
  }
}

/**
 * What the sign-in, sign-up, reset and verify forms say when the request
 * itself failed. A limit is a limit (auth §12: back off, don't re-render the
 * form), and only a request that got no answer is blamed on the connection.
 */
export function authFailureCopy(error: unknown): Message {
  const code = codeFromError(error);
  if (code === 'RATE_LIMITED') return msg('refusal-auth-rate-limited', { wait: waitCopy(retryAfterFromError(error)) });
  if (code === null) return msg('refusal-unreachable');
  return msg('refusal-auth-failed');
}

/** Where a signed-out visitor is meant to be: a mail link lands on two of them. */
const SIGNED_OUT_PAGES = ['/signup', '/verify', '/reset'];

/**
 * Where to send the browser for a GraphQL response, or `null` to stay.
 *
 * Auth §13: an `UNAUTHENTICATED` refusal means the session cookie expired, and
 * the learner should land on sign-in rather than on a feature reporting its own
 * failure. Never from a signed-out page: `me` answers `null` there rather than
 * refusing, so this would only fire on a stray call — and redirecting a mail
 * link's page away from itself would make the link unusable.
 */
export function signInRedirectFor(response: unknown, pathname: string): string | null {
  if (SIGNED_OUT_PAGES.includes(pathname)) return null;
  const errors = (response as { errors?: ReadonlyArray<{ extensions?: { code?: unknown } } | null> } | null)?.errors;
  return errors?.some((e) => e?.extensions?.code === 'UNAUTHENTICATED') ? '/signup' : null;
}
